import * as React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import { useAuth } from "../../../../app/providers/AuthProvider";
import {
  SIDEBAR_COLOR_MODE,
  setStoredSidebarDarkMode,
  useSidebarDarkMode,
} from "../../useAppLayoutAppearance";
import MenuContent, { APP_SHELL } from "./MenuContent";

const SIDEBAR_COLOR_TRANSITION =
  "background-color 180ms ease, border-color 180ms ease, color 180ms ease";

export default function SideMenu() {
  const { isAuthenticated } = useAuth();
  const sidebarDark = useSidebarDarkMode();
  const [compact, setCompact] = React.useState(false);
  const [contentCompact, setContentCompact] = React.useState(false);
  const [labelsVisible, setLabelsVisible] = React.useState(true);
  const labelTimer = React.useRef(null);
  const compactTimer = React.useRef(null);
  const contentTimer = React.useRef(null);

  React.useEffect(() => {
    return () => {
      if (labelTimer.current) {
        window.clearTimeout(labelTimer.current);
      }
      if (compactTimer.current) {
        window.clearTimeout(compactTimer.current);
      }
      if (contentTimer.current) {
        window.clearTimeout(contentTimer.current);
      }
    };
  }, []);

  if (!isAuthenticated) return null;

  const width = compact
    ? APP_SHELL.sidebarCompactWidth
    : APP_SHELL.sidebarExpandedWidth;

  function toggleCompact() {
    if (labelTimer.current) {
      window.clearTimeout(labelTimer.current);
    }
    if (compactTimer.current) {
      window.clearTimeout(compactTimer.current);
    }
    if (contentTimer.current) {
      window.clearTimeout(contentTimer.current);
    }

    if (compact) {
      setContentCompact(false);
      setCompact(false);
      labelTimer.current = window.setTimeout(() => {
        setLabelsVisible(true);
      }, 180);
      return;
    }

    setLabelsVisible(false);
    compactTimer.current = window.setTimeout(() => {
      setCompact(true);
      contentTimer.current = window.setTimeout(() => {
        setContentCompact(true);
      }, 320);
    }, 140);
  }

  return (
    <Box
      component="aside"
      sx={{
        display: { xs: "none", md: "block" },
        flexBasis: width,
        flexGrow: 0,
        flexShrink: 0,
        height: `calc(100vh - ${APP_SHELL.topbarHeight}px)`,
        maxWidth: width,
        minWidth: width,
        position: "sticky",
        top: APP_SHELL.topbarHeight,
        transition: [
          "flex-basis 320ms cubic-bezier(0.4, 0, 0.2, 1)",
          "max-width 320ms cubic-bezier(0.4, 0, 0.2, 1)",
          "min-width 320ms cubic-bezier(0.4, 0, 0.2, 1)",
          "width 320ms cubic-bezier(0.4, 0, 0.2, 1)",
        ].join(", "),
        width,
        zIndex: (theme) => theme.zIndex.drawer,
      }}
    >
      <MenuContent
        compact={contentCompact}
        labelVisible={labelsVisible}
        shellCompact={compact}
        sidebarDark={sidebarDark}
        onToggleDarkMode={() => {
          if (!SIDEBAR_COLOR_MODE.toggleEnabled) return;
          setStoredSidebarDarkMode(!sidebarDark);
        }}
      />

      <Tooltip title={compact ? "Expandir menu" : "Recolher menu"} placement="right">
        <ButtonBase
          type="button"
          aria-label={compact ? "Expandir menu" : "Recolher menu"}
          onClick={toggleCompact}
          sx={(theme) => ({
            alignItems: "center",
            bgcolor: sidebarDark
              ? theme.palette.custom.gray[800]
              : theme.palette.background.paper,
            border: `1px solid ${
              sidebarDark ? theme.palette.custom.gray[700] : theme.palette.border.default
            }`,
            borderRadius: "50%",
            color: sidebarDark ? theme.palette.custom.gray[300] : theme.palette.text.secondary,
            display: "flex",
            height: 28,
            justifyContent: "center",
            position: "absolute",
            right: -14,
            top: 18,
            transition: SIDEBAR_COLOR_TRANSITION,
            width: 28,
            zIndex: theme.zIndex.drawer + 1,
            "&:hover": {
              bgcolor: sidebarDark
                ? theme.palette.custom.gray[700]
                : theme.palette.action.hover,
              color: sidebarDark ? theme.palette.custom.gray[50] : theme.palette.text.primary,
            },
          })}
        >
          {compact ? (
            <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
          ) : (
            <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
          )}
        </ButtonBase>
      </Tooltip>
    </Box>
  );
}
